import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { t } from "i18next";

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { MuiCol, MuiRow } from "../../../../components/layout";
import { ScannerInput, MuiTextField, MuiSelect } from '../../../../components/inputs';
import { MuiButton } from "../../../../components/mui";


/**
 * Normalizes a barcode for comparison.
 *
 * Scanners and stored values disagree in two ways that matter here: a UPC-A
 * may come back as a 13-digit EAN with a leading zero, and stored values
 * sometimes carry spaces or dashes. Stripping non-digits and leading zeros
 * makes "012345678905", "12345678905" and "0 12345678905" all compare equal.
 */
function normalizeUpc(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "").replace(/^0+/, "");
}

/**
 * Manual quantity entry for loose items — non-serialized goods that arrive
 * pre-counted with no pallet and no masterpack, so there is nothing to scan
 * into the shipment the way containers are scanned.
 *
 * The operator picks a model (or scans its UPC, which is the same barcode
 * printed on the masterpack), picks the PO it belongs to, and types a
 * quantity. On confirm the parent receives one loose line and decides where
 * it lands in shipmentDoc.looseItems.
 *
 * The UPC scan lives inside this dialog rather than on the main scanner
 * because a UPC is 12–13 digits and would otherwise be swallowed by the
 * masterpack branch's /^\d{10,16}$/ test on the shipping screen.
 *
 * PO IDENTIFIERS: poOptions carry poId (NetSuite internal ID) as their value
 * and poNumber (human-readable) as their label. Both are handed back to the
 * parent separately — see the PO IDENTIFIERS note in the shipping screen.
 */
function LooseItemDialog({ isOpen, onClose, onAdd, models = [], poOptions = [], poLoading = false, ...props }) {
  const [scannedUpc, setScannedUpc] = useState("");
  const [scanError, setScanError] = useState("");
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedPoId, setSelectedPoId] = useState(null);
  const [quantity, setQuantity] = useState("");

  // Start clean every time the dialog opens — a stale selection from the
  // previous entry is worse than no selection.
  useEffect(() => {
    if (!isOpen) return;
    setScannedUpc("");
    setScanError("");
    setSelectedSku(null);
    setSelectedPoId(null);
    setQuantity("");
  }, [isOpen]);

  // Changing the model invalidates the PO: the PO list is scoped to the
  // chosen item, so a PO picked for a previous model may not carry this one.
  useEffect(() => {
    setSelectedPoId(null);
  }, [selectedSku]);

  const modelOptions = models.map(m => ({
    label: m.name ? `${m.name} — ${m.itemSku}` : m.itemSku,
    value: m.itemSku,
  }));

  const selectedModel = models.find(m => m.itemSku === selectedSku) || null;

  const quantityNumber = Number(quantity);
  const quantityValid = quantity !== "" && Number.isFinite(quantityNumber) && quantityNumber > 0;
  const canAdd = !!selectedSku && !!selectedPoId && quantityValid;

  function handleScanKeyDown(e) {
    if (e.key !== 'Enter') return;

    const scan = (e.target.value || "").trim();
    setScanError("");

    if (!scan) return;

    const scanKey = normalizeUpc(scan);

    // UPC first, then fall back to the SKU itself — operators scanning a
    // masterpack label may catch either barcode, and a SKU match is
    // unambiguous enough to accept.
    const match =
      (scanKey && models.find(m => normalizeUpc(m.upc) === scanKey)) ||
      models.find(m => String(m.itemSku).toUpperCase() === scan.toUpperCase());

    if (!match) {
      setScanError(t("shipping_cm.loose_unknown_upc"));
      setScannedUpc("");
      return;
    }

    setSelectedSku(match.itemSku);
    setScannedUpc("");
  }

  function handleAdd() {
    if (!canAdd) return;

    const po = poOptions.find(opt => opt.value === selectedPoId);

    onAdd({
      poId: selectedPoId,
      // Display-only. Blank rather than the ID when the option carries no
      // label — the PO number loader fills it in later.
      poNumber: po?.label ?? "",
      itemNetsuiteId: selectedModel?.itemNetsuiteId ?? null,
      itemSku: selectedSku,
      quantity: quantityNumber,
    });
  }

  return (
    <Dialog open={isOpen} onClose={onClose} disableEnforceFocus fullWidth maxWidth="sm">
      <DialogTitle>{t("shipping_cm.loose_item_title")}</DialogTitle>
      <DialogContent>
        <MuiRow spacing={1} sx={{ mt: 1 }}>
          <MuiCol size={12}>
            <ScannerInput
              onChange={setScannedUpc}
              onKeyDown={handleScanKeyDown}
              value={scannedUpc}
              label={t("shipping_cm.loose_scan_upc")}
              error={scanError}
            />
          </MuiCol>

          <MuiCol size={12}>
            <MuiSelect
              options={modelOptions}
              placeholder={t("shipping_cm.model")}
              value={modelOptions.find(opt => opt.value === selectedSku) || null}
              onChange={opt => setSelectedSku(opt?.value ?? null)}
            />
          </MuiCol>

          <MuiCol size={12}>
            <MuiSelect
              disabled={poLoading}
              options={poOptions}
              placeholder={poLoading ? t("loading") : t("shipping_cm.purchase_order")}
              value={poOptions.find(opt => opt.value === selectedPoId) || null}
              onChange={opt => setSelectedPoId(opt?.value ?? null)}
            />
          </MuiCol>

          <MuiCol size={12}>
            <MuiTextField
              type="number"
              value={quantity}
              onChange={setQuantity}
              label={t("shipping_cm.quantity")}
              error={quantity !== "" && !quantityValid}
              helperText={quantity !== "" && !quantityValid ? t("shipping_cm.loose_quantity_positive") : undefined}
            />
          </MuiCol>
        </MuiRow>
      </DialogContent>
      <DialogActions>
        <MuiButton color="primary" onClick={onClose}>{t("close")}</MuiButton>
        <MuiButton color="primary" disabled={!canAdd} onClick={handleAdd}>
          {t("shipping_cm.add")}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}

LooseItemDialog.propTypes = {
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  auth: state.auth,
  errors: state.errors
});

export default compose(
  withTranslation(),
  connect(
    mapStateToProps,
  )
)(LooseItemDialog);